import { Map } from 'immutable';
import { debug } from '../../services/logger';
import { IBotState, IBotLaunchMeta } from 'app/services/rtc/BotState';
import { RtcBotType } from '../../constants/enums/RtcBotType';
import Peer from '../../services/rtc/Peer';
import createStateContext from 'app/utils/createStateContext';
import SubscribableState from 'app/utils/SubscribableState';

type BotIdCollection = Map<Peer['id'], RtcBotType>;
type BotTypeCollection = Map<RtcBotType, Peer['id']>;
type BotStateCollection = Map<Peer['id'], Readonly<IBotState>>;
type BotMetaCollection = Map<Peer['id'], Readonly<IBotLaunchMeta>>;

interface IState {
  botsById: BotIdCollection;
  botsByType: BotTypeCollection;
  botStates: BotStateCollection;
  botMetas: BotMetaCollection;
}

const initialState: IState = {
  botsById: Map(),
  botsByType: Map(),
  botStates: Map(),
  botMetas: Map(),
};

const { Provider, useStore, useStoreNoSubscribe } = createStateContext<IState>();
export {
  Provider as BotStoreProvider,
  useStore as useBotStore,
  useStoreNoSubscribe as useBotStoreNoSubscribe,
};

export const createBotStore = () => new SubscribableState<IState>(initialState);

export type BotStore = SubscribableState<IState>;

// Getters

const getState = (store: BotStore | undefined) => store?.state ?? initialState;

export const getBotPeerIdForType = (store: BotStore | undefined, botType: RtcBotType) => {
  return getState(store).botsByType.get(botType);
};

export const getBotStateForType = (store: BotStore | undefined, botType: RtcBotType) => {
  const { botsByType, botStates } = getState(store);
  const peerId = botsByType.get(botType);

  return peerId ? botStates.get(peerId) : undefined;
};

export const getBotMetaForType = (store: BotStore | undefined, botType: RtcBotType) => {
  const { botsByType, botMetas } = getState(store);
  const peerId = botsByType.get(botType);

  return peerId ? botMetas.get(peerId) : undefined;
};

// Actions

/**
 * Resets the state.
 */
export const resetBotStore = (store: BotStore) => {
  store.setState(initialState);
};

/**
 * Deletes associated bot state and undesignates a bot if it is in the botType table.
 */
export const deleteBot = (store: BotStore, peerId: Peer['id']) => {
  const { botsById, botsByType, botStates, botMetas } = store.state;
  const botType = botsById.get(peerId);

  store.setState({
    botsById:   botsById.delete(peerId),
    botsByType: botType === undefined ? botsByType : botsByType.delete(botType),
    botStates:  botStates.delete(peerId),
    botMetas:   botMetas.delete(peerId),
  });
};

/**
 * This establishes that a bot with the given peerId should be treated as a specific
 * botType.
 */
export const designateBotAsType = (store: BotStore, peerId: Peer['id'], botType: RtcBotType) => {
  debug(`Listening for bot of type, ${botType}, on peer id: ${peerId}.`);

  const { botsByType, botsById } = store.state;

  if (botsByType.has(botType) && botsByType.get(botType) !== peerId) {
    const existingPeerId = botsByType.get(botType);

    throw new Error(
      `Attempting to launch ${botType} bot with peerId ${peerId}.` +
      `But ${existingPeerId} already exists.`,
    );
  }

  store.setState({
    botsById:   botsById.set(peerId, botType),
    botsByType: botsByType.set(botType, peerId),
  });
};

export const setBotMeta = (store: BotStore, peerId: Peer['id'], meta: IBotLaunchMeta) => {
  const { botMetas } = store.state;
  store.setState({ botMetas: botMetas.set(peerId, meta) });
};

/**
 * This function is intended to be called synchronously.
 */
export const setBotState = (store: BotStore, peerId: Peer['id'], botState: IBotState) => {
  debug(`Received new bot state.`, botState);

  // Always maintain the bot state, since we might want to designate a bot after it has
  // joined the room (ex, slow launch resolve or reclaiming a bot).
  store.setState({
    botStates: store.state.botStates.set(peerId, botState),
  });
};
